import { useCallback, useEffect, useState } from 'react'
import { Flex } from '@mantine/core'
import { useIsMutating } from '@tanstack/react-query'

import type {
  Field,
  FieldConditions,
} from '@/modules/onboarding/pages/Onboarding.types'

import { Button } from '@/components/Button/Button'
import { Disclaimer } from '@/components/Disclaimer/Disclaimer'
import { NumberInput } from '@/components/NumberInput/NumberInput'
import { RadioInput } from '@/components/RadioInput/RadioInput'
import { SelectInput } from '@/components/SelectInput/SelectInput'
import { SeparateDateInput } from '@/components/SeparateDateInput/SeparateDateInput'
import { Text } from '@/components/Text/Text'
import { TextInput } from '@/components/TextInput/TextInput'
import { Title } from '@/components/Title/Title'

import type { Answer } from '@/__generated__/requests'

import classes from './OnboardingForm.module.css'
import type { OnboardingFormProps } from './OnboardingForm.types'

export function OnboardingForm({
  currentStep,
  finishStep,
  hideDisclaimer = false,
  highlightFinancial = false,
  isFinal,
  prevAnswers,
  previousStep,
  step,
}: OnboardingFormProps) {
  const [values, setValues] = useState<Answer[]>([])
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const isStepInitialized = values.length === step.fields.length

  const isMutating = useIsMutating({
    exact: true,
  })

  const handleChange = (
    val: string,
    i: number,
    conditions?: FieldConditions,
  ) => {
    const newValues = [...values]

    newValues[i] = {
      answer: val,
      fieldName: values[i].fieldName,
      isBirthDate: values[i].isBirthDate,
      isFinancial: values[i].isFinancial,
      isGenderField: values[i].isGenderField,
      isHidden: values[i].isHidden,
      isIncomeField: values[i].isIncomeField,
      isRaceField: values[i].isRaceField,
      question: values[i].question,
    }

    if (val) {
      setFieldErrors(prev => {
        const next = { ...prev }

        delete next[values[i].fieldName]

        return next
      })
    }

    if (!conditions) {
      setValues(newValues)

      return
    }

    const conditionalValues = newValues.map(value => {
      if (conditions.fields.includes(value.fieldName)) {
        return { ...value, isHidden: val !== conditions.value }
      }

      return value
    })

    setValues(conditionalValues)
  }

  const validateFinancialFields = (): boolean => {
    if (!highlightFinancial) return true

    const errors: Record<string, string> = {}

    // Prevent advancing while the current step answers are still initializing.
    if (!isStepInitialized) {
      return false
    }

    const answersByFieldName = values.reduce<Record<string, Answer>>(
      (acc, answer) => {
        acc[answer.fieldName] = answer

        return acc
      },
      {},
    )

    step.fields.forEach(field => {
      const answer = answersByFieldName[field.field_name]

      if (
        field.isFinancial &&
        answer &&
        !answer.isHidden &&
        !answer.answer.trim()
      ) {
        errors[field.field_name] = 'This field is required to access planners'
      }
    })

    setFieldErrors(errors)

    return Object.keys(errors).length === 0
  }

  const getInitAnswer = useCallback(
    (field: Field) => {
      if (!prevAnswers) return ''

      const initAnswer = prevAnswers.find(
        (answer: Answer) => answer.fieldName === field.field_name,
      )

      if (!initAnswer) return ''

      return initAnswer.answer
    },
    [prevAnswers],
  )

  const getInitHidden = useCallback(
    (field: Field) => {
      if (field.visibleWhen && prevAnswers) {
        const dependentAnswer = prevAnswers.find(
          (answer: Answer) => answer.fieldName === field.visibleWhen!.fieldName,
        )

        return (
          !dependentAnswer || dependentAnswer.answer !== field.visibleWhen.value
        )
      }

      if (!prevAnswers) return field.isHidden

      const initAnswer = prevAnswers.find(
        (answer: Answer) => answer.fieldName === field.field_name,
      )

      if (!initAnswer) return field.isHidden

      return initAnswer.isHidden
    },
    [prevAnswers],
  )

  const getInitDate = (i: number) => {
    if (values[i] && values[i].answer !== '') return new Date(values[i].answer)

    return undefined
  }

  useEffect(() => {
    const answers: Answer[] = step.fields.map(field => ({
      answer: getInitAnswer(field),
      fieldName: field.field_name,
      isBirthDate: field.isBirthDate,
      isFinancial: field.isFinancial,
      isGenderField: field.isGenderField,
      isHidden: getInitHidden(field),
      isIncomeField: field.isIncomeField,
      isRaceField: field.isRaceField,
      question: field.question,
    }))

    setValues(answers)
    setFieldErrors({})
  }, [getInitAnswer, getInitHidden, step.fields])

  return (
    <>
      {step.title && (
        <Title ta="center" variant="h2">
          {step.title}
        </Title>
      )}

      {step.disclaimer && !hideDisclaimer && (
        <Disclaimer
          description={step.disclaimer.description}
          title={step.disclaimer.title}
        />
      )}

      {step.fields.map((field, i) => (
        <Flex
          key={`${field.question}-${field.type}`}
          className={classes.inputBox}
        >
          {values[i] && !values[i].isHidden && (
            <Text className={classes.label} variant="body_2">
              {field.question}
            </Text>
          )}

          {field.type === 'input' && values[i] && !values[i].isHidden && (
            <TextInput
              classNames={{
                wrapper:
                  highlightFinancial &&
                  field.isFinancial &&
                  values[i] &&
                  !values[i].answer
                    ? classes.inputFinancial
                    : '',
              }}
              error={fieldErrors[field.field_name]}
              onChange={e => handleChange(e.target.value, i)}
              placeholder={field.placeholder}
              value={values[i] && values[i].answer}
            />
          )}

          {field.type === 'number' && values[i] && !values[i].isHidden && (
            <NumberInput
              allowNegative={false}
              classNames={{
                wrapper:
                  highlightFinancial &&
                  field.isFinancial &&
                  values[i] &&
                  !values[i].answer
                    ? classes.inputFinancial
                    : '',
              }}
              decimalScale={2}
              error={fieldErrors[field.field_name]}
              hideControls
              onChange={e => {
                if (e === '.' || e === '0.') return
                handleChange(e.toString(), i)
              }}
              placeholder={field.placeholder}
              value={
                values[i] && values[i].answer && parseFloat(values[i].answer)
              }
            />
          )}

          {field.type === 'date' && values[i] && !values[i].isHidden && (
            <SeparateDateInput
              disableFutureDates
              gap={10}
              onChange={e => {
                handleChange(e.toString(), i)
              }}
              value={getInitDate(i)}
            />
          )}
          {field.type === 'select' && (
            <SelectInput
              data={field.options}
              error={fieldErrors[field.field_name]}
              onChange={e => {
                if (!e) return
                handleChange(e, i)
              }}
              placeholder={field.placeholder}
              value={values[i] && values[i].answer}
            />
          )}
          {field.type === 'radio' &&
            field.options &&
            values[i] &&
            !values[i].isHidden && (
              <>
                <RadioInput
                  classNames={{
                    radio:
                      highlightFinancial &&
                      field.isFinancial &&
                      values[i] &&
                      !values[i].answer
                        ? classes.radioFinancial
                        : '',
                  }}
                  data={field.options}
                  initValue={values[i] && values[i].answer}
                  onChange={e =>
                    handleChange(e.target.value, i, field.conditions)
                  }
                />
                {fieldErrors[field.field_name] && (
                  <Text c="red" fz="xs">
                    {fieldErrors[field.field_name]}
                  </Text>
                )}
              </>
            )}
        </Flex>
      ))}
      <Flex className={classes.buttonsContainer} gap={10}>
        {currentStep > 0 && (
          <Button
            disabled={!!isMutating}
            onClick={() => previousStep(values)}
            variant="outline"
          >
            Previous
          </Button>
        )}
        <Button
          disabled={!isStepInitialized || !!isMutating}
          loading={!!isMutating}
          onClick={() => {
            if (validateFinancialFields()) {
              finishStep(values)
            }
          }}
          type="submit"
          variant="primary"
        >
          {isFinal ? 'Finish' : 'Next'}
        </Button>
      </Flex>
    </>
  )
}
